import * as React from "react";
import { get, useFormState } from "react-hook-form";

import { type ParameterFieldParameter } from "@/shared/components/ResourceForm";
import { Collapsible } from "@/shared/foundation/primitives";

import { ParameterRow } from "../ParameterRow";

import styles from "./parameterForms.module.css";
import { useParameterRecord } from "./parameterValues";
import { type ParameterFormProps } from "./types";

export interface MoreParametersProps extends ParameterFormProps {
  /**
   * The keys the form handles itself, whether it has a control for them or
   * deliberately offers none. None of them is listed here.
   */
  coveredKeys: readonly string[];
  /** The keys of `children`, so an error on one opens the disclosure too. */
  disclosedKeys?: readonly string[];
  /** The form's own controls for settings few people change, listed first. */
  children?: React.ReactNode;
}

/**
 * A disclosure under a form written for one action: the form's own controls
 * for settings few people change, then every parameter the form does not
 * cover: ones the action declares that the form leaves out,
 * and keys the action does not declare but a stored spec carries, so none is
 * lost on save.
 *
 * Starts closed unless a listed parameter is required, and opens on its own
 * while anything in it has a validation error, so an error is never hidden.
 */
export function MoreParameters({
  name,
  action,
  templateCatalog,
  coveredKeys,
  disclosedKeys = [],
  children,
}: MoreParametersProps) {
  const record = useParameterRecord(name);
  const { errors } = useFormState({ name });
  const declaredParameters = action.parameters ?? [];
  const declared = declaredParameters.filter(
    (parameter) => !coveredKeys.includes(parameter.name),
  );
  const undeclared: ParameterFieldParameter[] = Object.keys(record.values)
    .filter(
      (key) =>
        !coveredKeys.includes(key) &&
        !declaredParameters.some((parameter) => parameter.name === key),
    )
    .map((key) => ({
      name: key,
      required: false,
      description: "Not declared by this action.",
    }));
  const listed: ParameterFieldParameter[] = [...declared, ...undeclared];
  const errorOf = (key: string) =>
    (get(errors, `${name}.${key}`) as { message?: string } | undefined)
      ?.message;
  const hasError = [
    ...disclosedKeys,
    ...listed.map((parameter) => parameter.name),
  ].some((key) => errorOf(key) !== undefined);
  const [open, setOpen] = React.useState(() =>
    listed.some((parameter) => parameter.required),
  );

  if (listed.length === 0 && children === undefined) {
    return null;
  }
  return (
    <Collapsible.Root
      open={open || hasError}
      onOpenChange={setOpen}
      data-testid="more-parameters"
    >
      <h5 className={styles.disclosureTitle}>
        <Collapsible.Trigger>More parameters</Collapsible.Trigger>
      </h5>
      <Collapsible.Panel className={styles.disclosurePanel}>
        {children}
        {listed.map((parameter) => (
          <ParameterRow
            key={parameter.name}
            name={name}
            parameter={parameter}
            templateCatalog={templateCatalog}
            error={errorOf(parameter.name)}
          />
        ))}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}
